/*
 * Copyright 2026 North Carolina State University
 */

import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import {
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
  Box,
  IconButton,
  Typography,
  Chip,
  Paper,
  Divider,
  Collapse,
  Tooltip
} from '@mui/material';
import { 
  FirstPage, 
  LastPage, 
  KeyboardArrowLeft, 
  KeyboardArrowRight,
  Search as SearchIcon,
  Delete as DeleteIcon,
  Add as AddIcon,
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
  Info as InfoIcon
} from '@mui/icons-material';
import useTableSort from '../../hooks/useTableSort';
import SortableTableHeader from '../shared/SortableTableHeader';
import useFacility from '../../hooks/useFacility';
import { toast } from 'react-toastify';
import { formatHoursLabel, formatHoursConversion } from '../../utils/timeConversion';
import { formatRate } from '../../utils/ratePricing';
import CompartmentTypeSelect from '../shared/CompartmentTypeSelect';

const getChamberCapacityInfo = (chamber) => {
  return {
    max_capacity: chamber.capacity?.max_capacity || 0,
    unit_name: chamber.capacity?.unit?.base_unit || 'Not Set',
    unit_short: chamber.capacity?.unit?.unit_short || ''
  };
};

// These are calendar-date fields (install/activation/deactivation dates) entered
// via <input type="date"> and stored as UTC midnight. Format in UTC so the
// displayed day matches exactly what the admin entered (no timezone shift).
const formatDate = (dateStr) => {
  if (!dateStr) return '—';
  return new Date(dateStr).toLocaleDateString('en-US', {
    year: 'numeric', month: 'short', day: 'numeric', timeZone: 'UTC'
  });
};

const toInputDate = (dateStr) => {
  if (!dateStr) return '';
  const d = new Date(dateStr);
  return d.toISOString().split('T')[0];
};

const hasAssetInfo = (chamber) => {
  return chamber.model_number || chamber.serial_number || 
         chamber.asset_management_number || chamber.activation_date || 
         chamber.deactivation_date || chamber.location || chamber.comments ||
         (chamber.lights && chamber.lights.length > 0);
};

const ChamberManagement = () => {
  const { facilityPreference, loading: prefLoading } = useFacility();
  
  const [chambers, setChambers] = useState([]);
  const [facilities, setFacilities] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedFacility, setSelectedFacility] = useState('all');
  const [filteredChambers, setFilteredChambers] = useState([]);
  const [units, setUnits] = useState([]);
  const [lighting, setLighting] = useState([]);
  
  useEffect(() => {
    if (!prefLoading && facilityPreference) {
      setSelectedFacility(facilityPreference);
    }
  }, [facilityPreference, prefLoading]);
  
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [editingChamber, setEditingChamber] = useState(null);
  const [newChamber, setNewChamber] = useState({
    chamber: '',
    description: '',
    facility: '',
    capacity: { unit: '', max_capacity: 0 },
    rate_limits: [],
    model_number: '',
    serial_number: '',
    asset_management_number: '',
    activation_date: '',
    deactivation_date: '',
    location: '',
    comments: '',
    lights: [],
    compartment_types: []
  });

  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [searchQuery, setSearchQuery] = useState('');
  const [expandedRow, setExpandedRow] = useState(null);

  const { sortedData, sortColumn, sortOrder, handleSort, resetSort } = useTableSort(
    filteredChambers,
    { defaultSortColumn: 'chamber', defaultSortOrder: 'asc' }
  );

  const fetchData = useCallback(async () => {
    try {
      const [chambersRes, facilitiesRes, unitsRes, lightingRes] = await Promise.all([
        axios.get('/api/chambers'),
        axios.get('/api/facilities'),
        axios.get('/api/units'),
        axios.get('/api/lighting')
      ]);
      
      let fetchedChambers = chambersRes.data;
      if (selectedFacility !== 'all') {
        fetchedChambers = fetchedChambers.filter(
          chamber => chamber.facility?._id === selectedFacility
        );
      }
      
      setChambers(fetchedChambers);
      setFilteredChambers(fetchedChambers);
      setFacilities(facilitiesRes.data);
      setUnits(unitsRes.data);
      setLighting(lightingRes.data);
      setLoading(false);
    } catch (error) {
      console.error('Error details:', error);
      setError('Failed to fetch data');
      setLoading(false);
    }
  }, [selectedFacility]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  useEffect(() => {
    const filtered = chambers.filter(chamber => {
      const searchLower = searchQuery.toLowerCase();
      return chamber.chamber?.toLowerCase().includes(searchLower) ||
             chamber.description?.toLowerCase().includes(searchLower) ||
             chamber.facility?.facility_name?.toLowerCase().includes(searchLower) ||
             chamber.location?.toLowerCase().includes(searchLower);
    });
    setFilteredChambers(filtered);
    setPage(0);
  }, [searchQuery, chambers]);

  const handleChangePage = (newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const startIndex = page * rowsPerPage;
  const endIndex = Math.min(startIndex + rowsPerPage, sortedData.length);
  const paginatedChambers = sortedData.slice(startIndex, endIndex);

  const handleCreate = async () => {
    try {
      if (!newChamber.chamber || !newChamber.description || !newChamber.facility) {
        alert('Please fill in all required fields');
        return;
      }

      if (!newChamber.capacity.unit || !newChamber.capacity.max_capacity) {
        alert('Please select a unit and set max capacity');
        return;
      }

      const chamberData = {
        chamber: newChamber.chamber.trim(),
        description: newChamber.description.trim(),
        facility: newChamber.facility,
        capacity: {
          unit: newChamber.capacity.unit,
          max_capacity: parseInt(newChamber.capacity.max_capacity) || 0
        },
        rate_limits: newChamber.rate_limits || [],
        model_number: newChamber.model_number || null,
        serial_number: newChamber.serial_number || null,
        asset_management_number: newChamber.asset_management_number || null,
        activation_date: newChamber.activation_date || null,
        deactivation_date: newChamber.deactivation_date || null,
        location: newChamber.location || null,
        comments: newChamber.comments || null,
        lights: (newChamber.lights || []).filter(l => l.light_type),
        compartment_types: newChamber.compartment_types || []
      };

      await axios.post('/api/chambers', chamberData);
      await fetchData();
      toast.success('Compartment created successfully');
      setNewChamber({
        chamber: '',
        description: '',
        facility: '',
        capacity: { unit: '', max_capacity: 0 },
        rate_limits: [],
        model_number: '',
        serial_number: '',
        asset_management_number: '',
        activation_date: '',
        deactivation_date: '',
        location: '',
        comments: '',
        lights: [],
        compartment_types: []
      });
    } catch (error) {
      toast.error(error.response?.data?.error || 'Error creating compartment');
    }
  };

  const handleUpdate = async () => {
    try {
      const unitId = editingChamber.capacity?.unit?._id || editingChamber.capacity?.unit;
      
      const chamberData = {
        chamber: editingChamber.chamber,
        description: editingChamber.description,
        facility: editingChamber.facility?._id || editingChamber.facility,
        capacity: {
          unit: unitId,
          max_capacity: parseInt(editingChamber.capacity?.max_capacity) || 0
        },
        rate_limits: unitId ? (editingChamber.rate_limits || []) : [],
        model_number: editingChamber.model_number || null,
        serial_number: editingChamber.serial_number || null,
        asset_management_number: editingChamber.asset_management_number || null,
        activation_date: editingChamber.activation_date || null,
        deactivation_date: editingChamber.deactivation_date || null,
        location: editingChamber.location || null,
        comments: editingChamber.comments || null,
        lights: (editingChamber.lights || []).filter(l => l.light_type),
        compartment_types: (editingChamber.compartment_types || []).map(t => t._id || t)
      };

      await axios.put(`/api/chambers/${editingChamber._id}`, chamberData);
      await fetchData();
      setEditDialogOpen(false);
      setEditingChamber(null);
    } catch (error) {
      alert(error.response?.data?.error || error.message || 'Error saving compartment');
    }
  };

  const handleDelete = async (id) => {
    if (window.confirm('Are you sure you want to delete this compartment?')) {
      try {
        await axios.delete(`/api/chambers/${id}`);
        fetchData();
      } catch (error) {
        alert(error.response?.data?.error || 'Failed to delete compartment');
      }
    }
  };

  const handleEditClick = (chamber) => {
    const hasUnit = chamber.capacity?.unit?._id || chamber.capacity?.unit;
    setEditingChamber({ 
      ...chamber,
      rate_limits: hasUnit ? (chamber.rate_limits || []) : [],
      model_number: chamber.model_number || '',
      serial_number: chamber.serial_number || '',
      asset_management_number: chamber.asset_management_number || '',
      activation_date: toInputDate(chamber.activation_date),
      deactivation_date: toInputDate(chamber.deactivation_date),
      location: chamber.location || '',
      comments: chamber.comments || '',
      lights: (chamber.lights || []).map(l => ({
        light_type: l.light_type?._id || l.light_type || '',
        location: l.location || '',
        install_date: toInputDate(l.install_date)
      }))
    });
    setEditDialogOpen(true);
  };

  const getUnitsForFacility = (facilityId) => {
    if (!facilityId) return [];
    return units.filter(u => u.facility?._id === facilityId || u.facility === facilityId);
  };

  // Reusable location, comments and installed-lights fields for create/edit dialogs
  const renderLocationAndLights = (data, setData) => {
    const availableLights = lighting;
    const lights = data.lights || [];

    const handleAddLight = () => {
      setData({ ...data, lights: [...lights, { light_type: '', location: '', install_date: '' }] });
    };

    const handleRemoveLight = (index) => {
      setData({ ...data, lights: lights.filter((_, i) => i !== index) });
    };

    const handleUpdateLight = (index, field, value) => {
      setData({
        ...data,
        lights: lights.map((l, i) => (i === index ? { ...l, [field]: value } : l))
      });
    };

    return (
      <>
        <Divider sx={{ my: 2 }} />
        <Typography variant="h6" sx={{ mb: 1 }}>Location & Comments (Optional)</Typography>
        <TextField
          label="Location"
          fullWidth
          margin="normal"
          value={data.location || ''}
          onChange={(e) => setData({ ...data, location: e.target.value })}
          placeholder="e.g., Building 2, Room 104"
        />
        <TextField
          label="Description / Comments"
          fullWidth
          margin="normal"
          multiline
          minRows={2}
          value={data.comments || ''}
          onChange={(e) => setData({ ...data, comments: e.target.value })}
          placeholder="Additional notes about this compartment"
        />

        <Divider sx={{ my: 2 }} />
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
          <Typography variant="h6">Lighting (Optional)</Typography>
          <Button
            startIcon={<AddIcon />}
            size="small"
            onClick={handleAddLight}
            disabled={availableLights.length === 0}
          >
            Add Light
          </Button>
        </Box>
        {availableLights.length === 0 && (
          <Typography variant="body2" color="textSecondary">
            No light types defined yet. Add them under the Lighting tab first.
          </Typography>
        )}
        {lights.length > 0 && (
          <Paper variant="outlined" sx={{ p: 2 }}>
            {lights.map((light, index) => (
              <Box key={index} sx={{ display: 'flex', gap: 2, mb: 1, alignItems: 'center' }}>
                <FormControl size="small" sx={{ flex: 2 }}>
                  <InputLabel>Light Type</InputLabel>
                  <Select
                    value={light.light_type?._id || light.light_type || ''}
                    label="Light Type"
                    onChange={(e) => handleUpdateLight(index, 'light_type', e.target.value)}
                  >
                    {availableLights.map(opt => (
                      <MenuItem key={opt._id} value={opt._id}>
                        {opt.light_type}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
                <TextField
                  label="Location"
                  size="small"
                  sx={{ flex: 1 }}
                  value={light.location || ''}
                  onChange={(e) => handleUpdateLight(index, 'location', e.target.value)}
                  placeholder="e.g., Top shelf"
                />
                <TextField
                  label="Install Date"
                  type="date"
                  size="small"
                  sx={{ flex: 1 }}
                  InputLabelProps={{ shrink: true }}
                  value={light.install_date || ''}
                  onChange={(e) => handleUpdateLight(index, 'install_date', e.target.value)}
                />
                <IconButton aria-label="Delete" size="small" onClick={() => handleRemoveLight(index)}>
                  <DeleteIcon />
                </IconButton>
              </Box>
            ))}
          </Paper>
        )}
      </>
    );
  };

  // Reusable equipment info fields for create/edit dialogs
  const renderEquipmentFields = (data, setData) => (
    <>
      <Divider sx={{ my: 2 }} />
      <Typography variant="h6" sx={{ mb: 1 }}>Equipment & Asset Info (Optional)</Typography>
      <Box sx={{ display: 'flex', gap: 2 }}>
        <TextField
          label="Model Number"
          fullWidth
          margin="normal"
          value={data.model_number || ''}
          onChange={(e) => setData({ ...data, model_number: e.target.value })}
          placeholder="e.g., GC-15"
        />
        <TextField
          label="Serial Number"
          fullWidth
          margin="normal"
          value={data.serial_number || ''}
          onChange={(e) => setData({ ...data, serial_number: e.target.value })}
          placeholder="e.g., SN-20240101"
        />
      </Box>
      <TextField
        label="Asset Management Number"
        fullWidth
        margin="normal"
        value={data.asset_management_number || ''}
        onChange={(e) => setData({ ...data, asset_management_number: e.target.value })}
        placeholder="e.g., AST-0042"
      />
      <Box sx={{ display: 'flex', gap: 2 }}>
        <TextField
          label="Activation Date"
          type="date"
          fullWidth
          margin="normal"
          value={data.activation_date || ''}
          onChange={(e) => setData({ ...data, activation_date: e.target.value })}
          InputLabelProps={{ shrink: true }}
        />
        <TextField
          label="Deactivation Date"
          type="date"
          fullWidth
          margin="normal"
          value={data.deactivation_date || ''}
          onChange={(e) => setData({ ...data, deactivation_date: e.target.value })}
          InputLabelProps={{ shrink: true }}
        />
      </Box>
    </>
  );

  const renderCreateDialog = () => {
    const facilityUnits = getUnitsForFacility(newChamber.facility);
    const selectedUnit = units.find(u => u._id === newChamber.capacity.unit);
    const rates = selectedUnit?.rates || [];
    
    const handleAddRateLimit = (rateId) => {
      if (!newChamber.rate_limits.find(l => l.rate_id === rateId)) {
        setNewChamber({
          ...newChamber,
          rate_limits: [...newChamber.rate_limits, { rate_id: rateId, soft_limit: '', note: '' }]
        });
      }
    };

    const handleRemoveRateLimit = (rateId) => {
      setNewChamber({
        ...newChamber,
        rate_limits: newChamber.rate_limits.filter(l => l.rate_id !== rateId)
      });
    };

    const handleUpdateRateLimit = (rateId, field, value) => {
      setNewChamber({
        ...newChamber,
        rate_limits: newChamber.rate_limits.map(l => 
          l.rate_id === rateId ? { ...l, [field]: value } : l
        )
      });
    };
    
    return (
      <Dialog 
        open={createDialogOpen} 
        onClose={() => setCreateDialogOpen(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>Add New Compartment</DialogTitle>
        <DialogContent>
          <TextField
            label="Compartment ID/Name"
            fullWidth
            margin="normal"
            placeholder="e.g., 2376, GH-01, A01"
            value={newChamber.chamber}
            onChange={(e) => setNewChamber({...newChamber, chamber: e.target.value})}
          />
          <TextField
            label="Description"
            fullWidth
            margin="normal"
            placeholder="e.g., Walk-in growth compartment, Greenhouse Bay 1"
            value={newChamber.description}
            onChange={(e) => setNewChamber({...newChamber, description: e.target.value})}
          />
          <FormControl fullWidth margin="normal">
            <InputLabel>Facility</InputLabel>
            <Select
              value={newChamber.facility}
              onChange={(e) => setNewChamber({
                ...newChamber, 
                facility: e.target.value,
                capacity: { unit: '', max_capacity: 0 },
                rate_limits: []
              })}
              label="Facility"
            >
              {facilities.map(facility => (
                <MenuItem key={facility._id} value={facility._id}>
                  {facility.facility_name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <CompartmentTypeSelect
            value={newChamber.compartment_types}
            onChange={(ids) => setNewChamber({ ...newChamber, compartment_types: ids })}
            helperText="Kinds of project this compartment can host. Optional; pick as many as apply."
          />

          <Divider sx={{ my: 2 }} />
          <Typography variant="h6">Capacity</Typography>
          
          <Box sx={{ display: 'flex', gap: 2, mt: 1 }}>
            <FormControl sx={{ flex: 2 }} disabled={!newChamber.facility}>
              <InputLabel>Unit Type</InputLabel>
              <Select
                value={newChamber.capacity.unit}
                onChange={(e) => setNewChamber({
                  ...newChamber, 
                  capacity: { ...newChamber.capacity, unit: e.target.value },
                  rate_limits: []
                })}
                label="Unit Type"
              >
                {facilityUnits.map(unit => (
                  <MenuItem key={unit._id} value={unit._id}>
                    {unit.base_unit} ({unit.rates?.length || 0} rates)
                  </MenuItem>
                ))}
              </Select>
              {!newChamber.facility && (
                <Typography variant="caption" color="textSecondary">
                  Select a facility first
                </Typography>
              )}
            </FormControl>
            <TextField
              label="Max Capacity"
              type="number"
              sx={{ flex: 1 }}
              value={newChamber.capacity.max_capacity || ''}
              onChange={(e) => setNewChamber({
                ...newChamber,
                capacity: { ...newChamber.capacity, max_capacity: e.target.value }
              })}
              inputProps={{ min: 0 }}
              disabled={!newChamber.capacity.unit}
            />
          </Box>

          {rates.length > 0 && (
            <>
              <Divider sx={{ my: 2 }} />
              <Typography variant="h6">Limits (Optional)</Typography>
              <Typography variant="body2" color="textSecondary" sx={{ mb: 1 }}>
                Set soft limits for specific rates. These are advisory limits that admins can override.
              </Typography>
              
              <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mb: 2 }}>
                {rates.map(rate => {
                  const hasLimit = newChamber.rate_limits.find(l => l.rate_id === rate.rate_id);
                  return (
                    <Chip 
                      key={rate.rate_id}
                      label={`${rate.display_name}: $${rate.rate}/day`}
                      size="small"
                      color={hasLimit ? 'primary' : 'default'}
                      onClick={() => hasLimit ? handleRemoveRateLimit(rate.rate_id) : handleAddRateLimit(rate.rate_id)}
                      onDelete={hasLimit ? () => handleRemoveRateLimit(rate.rate_id) : undefined}
                    />
                  );
                })}
              </Box>

              {newChamber.rate_limits.length > 0 && (
                <Paper variant="outlined" sx={{ p: 2 }}>
                  {newChamber.rate_limits.map(limit => (
                    <Box key={limit.rate_id} sx={{ display: 'flex', gap: 2, mb: 1, alignItems: 'center' }}>
                      <Chip label={limit.rate_id} size="small" sx={{ minWidth: 100 }} />
                      <TextField
                        label="Limit"
                        type="number"
                        size="small"
                        value={limit.soft_limit || ''}
                        onChange={(e) => handleUpdateRateLimit(limit.rate_id, 'soft_limit', e.target.value)}
                        inputProps={{ min: 0 }}
                        sx={{ width: 120 }}
                      />
                      <TextField
                        label="Note (optional)"
                        size="small"
                        value={limit.note || ''}
                        onChange={(e) => handleUpdateRateLimit(limit.rate_id, 'note', e.target.value)}
                        sx={{ flex: 1 }}
                      />
                      <IconButton aria-label="Delete" size="small" onClick={() => handleRemoveRateLimit(limit.rate_id)}>
                        <DeleteIcon />
                      </IconButton>
                    </Box>
                  ))}
                </Paper>
              )}
            </>
          )}

          {renderEquipmentFields(newChamber, setNewChamber)}
          {renderLocationAndLights(newChamber, setNewChamber)}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCreateDialogOpen(false)}>Cancel</Button>
          <Button onClick={handleCreate} variant="contained" color="primary">
            Create Compartment
          </Button>
        </DialogActions>
      </Dialog>
    );
  };

  const renderEditDialog = () => {
    if (!editingChamber) return null;
    
    const facilityId = editingChamber.facility?._id || editingChamber.facility;
    const facilityUnits = getUnitsForFacility(facilityId);
    const selectedUnit = units.find(u => 
      u._id === (editingChamber.capacity?.unit?._id || editingChamber.capacity?.unit)
    );
    const rates = selectedUnit?.rates || [];

    const handleAddRateLimit = (rateId) => {
      if (!editingChamber.rate_limits?.find(l => l.rate_id === rateId)) {
        setEditingChamber({
          ...editingChamber,
          rate_limits: [...(editingChamber.rate_limits || []), { rate_id: rateId, soft_limit: '', note: '' }]
        });
      }
    };

    const handleRemoveRateLimit = (rateId) => {
      setEditingChamber({
        ...editingChamber,
        rate_limits: (editingChamber.rate_limits || []).filter(l => l.rate_id !== rateId)
      });
    };

    const handleUpdateRateLimit = (rateId, field, value) => {
      setEditingChamber({
        ...editingChamber,
        rate_limits: (editingChamber.rate_limits || []).map(l => 
          l.rate_id === rateId ? { ...l, [field]: value } : l
        )
      });
    };
    
    return (
      <Dialog 
        open={editDialogOpen} 
        onClose={() => setEditDialogOpen(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>Edit Compartment: {editingChamber.chamber}</DialogTitle>
        <DialogContent>
          <TextField
            label="Compartment ID/Name"
            fullWidth
            margin="normal"
            value={editingChamber.chamber || ''}
            onChange={(e) => setEditingChamber({...editingChamber, chamber: e.target.value})}
          />
          <TextField
            label="Description"
            fullWidth
            margin="normal"
            value={editingChamber.description || ''}
            onChange={(e) => setEditingChamber({...editingChamber, description: e.target.value})}
          />
          <FormControl fullWidth margin="normal">
            <InputLabel>Facility</InputLabel>
            <Select
              value={facilityId || ''}
              onChange={(e) => setEditingChamber({
                ...editingChamber, 
                facility: e.target.value,
                capacity: { unit: '', max_capacity: 0 },
                rate_limits: []
              })}
              label="Facility"
            >
              {facilities.map(facility => (
                <MenuItem key={facility._id} value={facility._id}>
                  {facility.facility_name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <CompartmentTypeSelect
            value={editingChamber.compartment_types || []}
            onChange={(ids) => setEditingChamber({ ...editingChamber, compartment_types: ids })}
            helperText="Kinds of project this compartment can host. Optional; pick as many as apply."
          />

          <Divider sx={{ my: 2 }} />

          <Typography variant="h6">Capacity</Typography>
          <Box sx={{ display: 'flex', gap: 2, mt: 1 }}>
            <FormControl sx={{ flex: 2 }} disabled={!facilityId}>
              <InputLabel>Unit Type</InputLabel>
              <Select
                value={editingChamber.capacity?.unit?._id || editingChamber.capacity?.unit || ''}
                onChange={(e) => setEditingChamber({
                  ...editingChamber,
                  capacity: { ...editingChamber.capacity, unit: e.target.value },
                  rate_limits: []
                })}
                label="Unit Type"
              >
                {facilityUnits.map(unit => (
                  <MenuItem key={unit._id} value={unit._id}>
                    {unit.base_unit} ({unit.rates?.length || 0} rates)
                  </MenuItem>
                ))}
              </Select>
              {!facilityId && (
                <Typography variant="caption" color="textSecondary">
                  Select a facility first
                </Typography>
              )}
            </FormControl>
            <TextField
              label="Max Capacity"
              type="number"
              sx={{ flex: 1 }}
              value={editingChamber.capacity?.max_capacity || ''}
              onChange={(e) => setEditingChamber({
                ...editingChamber,
                capacity: { ...editingChamber.capacity, max_capacity: e.target.value }
              })}
              inputProps={{ min: 0 }}
              disabled={!editingChamber.capacity?.unit}
            />
          </Box>

          {rates.length > 0 && (
            <>
              <Divider sx={{ my: 2 }} />
              <Typography variant="h6">Limits (Optional)</Typography>
              <Typography variant="body2" color="textSecondary" sx={{ mb: 1 }}>
                Click a rate to add/remove a soft limit for this compartment.
              </Typography>
              
              <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mb: 2 }}>
                {rates.map(rate => {
                  const hasLimit = editingChamber.rate_limits?.find(l => l.rate_id === rate.rate_id);
                  return (
                    <Chip 
                      key={rate.rate_id}
                      label={`${rate.display_name}: ${formatRate(rate.rate)}/${rate.rate_basis || 'day'}`}
                      size="small"
                      color={hasLimit ? 'primary' : 'default'}
                      onClick={() => hasLimit ? handleRemoveRateLimit(rate.rate_id) : handleAddRateLimit(rate.rate_id)}
                      onDelete={hasLimit ? () => handleRemoveRateLimit(rate.rate_id) : undefined}
                    />
                  );
                })}
              </Box>

              {editingChamber.rate_limits?.length > 0 && (
                <Paper variant="outlined" sx={{ p: 2 }}>
                  {editingChamber.rate_limits.map(limit => (
                    <Box key={limit.rate_id} sx={{ display: 'flex', gap: 2, mb: 1, alignItems: 'center' }}>
                      <Chip label={limit.rate_id} size="small" sx={{ minWidth: 100 }} />
                      <TextField
                        label="Limit"
                        type="number"
                        size="small"
                        value={limit.soft_limit || ''}
                        onChange={(e) => handleUpdateRateLimit(limit.rate_id, 'soft_limit', e.target.value)}
                        inputProps={{ min: 0 }}
                        sx={{ width: 120 }}
                      />
                      <TextField
                        label="Note (optional)"
                        size="small"
                        value={limit.note || ''}
                        onChange={(e) => handleUpdateRateLimit(limit.rate_id, 'note', e.target.value)}
                        sx={{ flex: 1 }}
                      />
                      <IconButton aria-label="Delete" size="small" onClick={() => handleRemoveRateLimit(limit.rate_id)}>
                        <DeleteIcon />
                      </IconButton>
                    </Box>
                  ))}
                </Paper>
              )}
            </>
          )}

          {renderEquipmentFields(editingChamber, setEditingChamber)}
          {renderLocationAndLights(editingChamber, setEditingChamber)}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditDialogOpen(false)}>Cancel</Button>
          <Button onClick={handleUpdate} variant="contained" color="primary">
            Save Changes
          </Button>
        </DialogActions>
      </Dialog>
    );
  };

  const renderPaginationControls = () => (
    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mt: 2 }}>
      <Box sx={{ display: 'flex', alignItems: 'center' }}>
        <span>Show</span>
        <Select
          value={rowsPerPage}
          onChange={handleChangeRowsPerPage}
          sx={{ mx: 1 }}
          size="small"
        >
          {[10, 25, 50, 100].map(n => (
            <MenuItem key={n} value={n}>{n}</MenuItem>
          ))}
        </Select>
        <span>entries</span>
      </Box>

      <Box sx={{ display: 'flex', alignItems: 'center' }}>
        <IconButton aria-label="First page" onClick={() => handleChangePage(0)} disabled={page === 0}>
          <FirstPage />
        </IconButton>
        <IconButton aria-label="Previous page" onClick={() => handleChangePage(page - 1)} disabled={page === 0}>
          <KeyboardArrowLeft />
        </IconButton>
        <Box sx={{ mx: 2 }}>
          Page {page + 1} of {Math.max(1, Math.ceil(filteredChambers.length / rowsPerPage))}
        </Box>
        <IconButton aria-label="Next page"
          onClick={() => handleChangePage(page + 1)}
          disabled={page >= Math.ceil(filteredChambers.length / rowsPerPage) - 1}
        >
          <KeyboardArrowRight />
        </IconButton>
        <IconButton aria-label="Last page"
          onClick={() => handleChangePage(Math.ceil(filteredChambers.length / rowsPerPage) - 1)}
          disabled={page >= Math.ceil(filteredChambers.length / rowsPerPage) - 1}
        >
          <LastPage />
        </IconButton>
      </Box>
    </Box>
  );

  const renderExpandedAssetInfo = (chamber) => {
    const lights = chamber.lights || [];
    return (
    <tr key={`${chamber._id}-detail`}>
      <td colSpan={7} style={{ padding: 0, border: 'none' }}>
        <Collapse in={expandedRow === chamber._id} timeout="auto" unmountOnExit>
          <Box sx={{ 
            px: 3, py: 2, 
            backgroundColor: '#f8f9fa', 
            borderBottom: '1px solid #dee2e6'
          }}>
            <Box sx={{ display: 'flex', gap: 4, flexWrap: 'wrap' }}>
              <Box>
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', fontWeight: 600 }}>
                  Model Number
                </Typography>
                <Typography variant="body2">
                  {chamber.model_number || '—'}
                </Typography>
              </Box>
              <Box>
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', fontWeight: 600 }}>
                  Serial Number
                </Typography>
                <Typography variant="body2">
                  {chamber.serial_number || '—'}
                </Typography>
              </Box>
              <Box>
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', fontWeight: 600 }}>
                  Asset Mgmt #
                </Typography>
                <Typography variant="body2">
                  {chamber.asset_management_number || '—'}
                </Typography>
              </Box>
              <Box>
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', fontWeight: 600 }}>
                  Activation Date
                </Typography>
                <Typography variant="body2">
                  {formatDate(chamber.activation_date)}
                </Typography>
              </Box>
              <Box>
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', fontWeight: 600 }}>
                  Deactivation Date
                </Typography>
                <Typography variant="body2">
                  {formatDate(chamber.deactivation_date)}
                </Typography>
              </Box>
              <Box>
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', fontWeight: 600 }}>
                  Location
                </Typography>
                <Typography variant="body2">
                  {chamber.location || '—'}
                </Typography>
              </Box>
            </Box>

            {chamber.comments && (
              <Box sx={{ mt: 2 }}>
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', fontWeight: 600 }}>
                  Description / Comments
                </Typography>
                <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>
                  {chamber.comments}
                </Typography>
              </Box>
            )}

            <Box sx={{ mt: 2 }}>
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', fontWeight: 600, mb: 0.5 }}>
                Lighting ({lights.length})
              </Typography>
              {lights.length === 0 ? (
                <Typography variant="body2" color="text.secondary">No lights installed</Typography>
              ) : (
                <table className="data-table" style={{ width: 'auto', minWidth: 420, marginTop: 4 }}>
                  <thead>
                    <tr>
                      <th>Light Type</th>
                      <th>Location</th>
                      <th>Install Date</th>
                      <th>Life Expectancy</th>
                    </tr>
                  </thead>
                  <tbody>
                    {lights.map((light, index) => {
                      const lifeHours = light.light_type?.life_expectancy;
                      return (
                      <tr key={light._id || index}>
                        <td>{light.light_type?.light_type || <em style={{ color: '#999' }}>Unknown</em>}</td>
                        <td>{light.location || '—'}</td>
                        <td>{formatDate(light.install_date)}</td>
                        <td>
                          {lifeHours != null && lifeHours !== '' ? (
                            <Tooltip title={formatHoursConversion(lifeHours)} arrow>
                              <span style={{ cursor: 'help', borderBottom: '1px dotted #999' }}>
                                {formatHoursLabel(lifeHours)}
                              </span>
                            </Tooltip>
                          ) : '—'}
                        </td>
                      </tr>
                      );
                    })}
                  </tbody>
                </table>
              )}
            </Box>
          </Box>
        </Collapse>
      </td>
    </tr>
    );
  };

  if (loading) return <div>Loading...</div>;
  if (error) return <div>Error: {error}</div>;

  return (
    <div className="chamber-management">
      <h2>Compartment Management</h2>
      <Typography variant="body2" color="textSecondary" sx={{ mb: 2 }}>
        Compartments have unified capacity tracking. Each compartment has one max capacity with multiple rate options available for billing. 
        Click the <InfoIcon sx={{ fontSize: 14, verticalAlign: 'middle' }} /> icon on any row to view equipment and asset details.
      </Typography>
      
      <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2, alignItems: 'center' }}>
        <TextField
          placeholder="Search compartments..."
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          sx={{ minWidth: 300 }}
          InputProps={{
            startAdornment: <SearchIcon sx={{ mr: 1, color: 'action.active' }} />
          }}
        />
        <FormControl sx={{ minWidth: 200 }}>
          <InputLabel>Filter by Facility</InputLabel>
          <Select
            value={selectedFacility}
            onChange={(e) => setSelectedFacility(e.target.value)}
            label="Filter by Facility"
          >
            <MenuItem value="all">All Facilities</MenuItem>
            {facilities.map(facility => (
              <MenuItem key={facility._id} value={facility._id}>
                {facility.facility_name}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      </Box>

      <Box sx={{ mb: 2, display: 'flex', gap: 2 }}>
        <Button
          variant="contained"
          color="primary"
          onClick={() => setCreateDialogOpen(true)}
        >
          Add New Compartment
        </Button>
        <Button
          variant="outlined"
          onClick={resetSort}
        >
          Reset Sort
        </Button>
      </Box>

      <table className="data-table">
        <thead>
          <tr>
            <SortableTableHeader column="chamber" label="Compartment" currentSortColumn={sortColumn} sortOrder={sortOrder} onSort={handleSort} />
            <SortableTableHeader column="description" label="Description" currentSortColumn={sortColumn} sortOrder={sortOrder} onSort={handleSort} />
            <SortableTableHeader column="facility.facility_name" label="Facility" currentSortColumn={sortColumn} sortOrder={sortOrder} onSort={handleSort} />
            <th>Types</th>
            <th>Unit</th>
            <th>Max Capacity</th>
            <th>Actions</th>
          </tr>
        </thead>
        <tbody>
          {paginatedChambers.map(chamber => {
            const info = getChamberCapacityInfo(chamber);
            return (
              <React.Fragment key={chamber._id}>
                <tr>
                  <td><strong>{chamber.chamber}</strong></td>
                  <td>{chamber.description}</td>
                  <td>{chamber.facility?.facility_name || <em style={{color: '#999'}}>Not set</em>}</td>
                  <td>{(chamber.compartment_types || []).map(t => t.name).filter(Boolean).join(', ') || '—'}</td>
                  <td>{info.unit_name}</td>
                  <td>{info.max_capacity} {info.unit_short}</td>
                  <td className="action-buttons">
                    <Tooltip title="View equipment & asset info">
                      <IconButton
                        size="small"
                        onClick={() => setExpandedRow(expandedRow === chamber._id ? null : chamber._id)}
                        sx={{ mr: 0.5 }}
                        color={hasAssetInfo(chamber) ? 'primary' : 'default'}
                      >
                        {expandedRow === chamber._id ? <ExpandLessIcon /> : <InfoIcon />}
                      </IconButton>
                    </Tooltip>
                    <Button
                      variant="contained"
                      size="small"
                      className="row-action-edit"
                  style={{ marginRight: '8px' }}
                      onClick={() => handleEditClick(chamber)}
                    >
                      Edit
                    </Button>
                    <Button
                      variant="contained"
                      size="small"
                      color="error"
                      onClick={() => handleDelete(chamber._id)}
                    >
                      Delete
                    </Button>
                  </td>
                </tr>
                {renderExpandedAssetInfo(chamber)}
              </React.Fragment>
            );
          })}
        </tbody>
      </table>

      {renderPaginationControls()}

      <Box sx={{ mt: 2, color: 'text.secondary' }}>
        Showing {Math.min(startIndex + 1, sortedData.length)} to {endIndex} of {sortedData.length} entries
        {searchQuery && ` (filtered from ${chambers.length} total entries)`}
      </Box>

      {renderCreateDialog()}
      {renderEditDialog()}
    </div>
  );
};

export default ChamberManagement;
